"use client"

/**
 * Pricing v5, the check-up builder: the membership-first site's /pricing
 * (Eugene, 30.09). The heading says how the pricing works, and the page
 * opens on the Baseline plan's card right under it, the way Everlab's How
 * it works opens on its entry product (research/pricing-pages/NOTES.md,
 * 6b). Then an MRI, then single tests, then how it works.
 *
 * - The picks and "More info" popups are the new booking's
 *   (pricing-v5-booking.tsx), so the page and the booking look alike. What
 *   they say comes from the site's product data, the CMS and the live
 *   prices (pricing-v5-items.ts, pricing-v5-page-data.ts).
 * - The plan card is the homepage's coloured baseline card as Eugene laid
 *   it out in Figma (pricing-v5-plan-card.tsx): the plan's promises and
 *   lines, its three prices, the booking button and "Learn more". How often
 *   is chosen in the booking, which has no cadence parameter.
 * - An MRI or a single test goes in with its Add button, not a tap on its
 *   card: the page is read before anything is bought (Eugene, 30.09). A bar
 *   at the bottom comes up with the first pick and adds it up on top of the
 *   plan, which is always in (see pricing-v5-shared.tsx). The plan card
 *   holds the booking button until then.
 * - Imaging tests need an MRI, as in the booking flow.
 * - Core is out of the plan flow (Eugene, 30.09). It stays in the one-time
 *   path behind the one-off link under the test lists.
 *
 * Two variants, same page otherwise:
 * - `all`: the MRI and single tests are both picked here (/pricing)
 * - `scan`: only the MRI is picked here; single tests show their prices and
 *   are added in the booking, as Aeon does. No route renders it since its
 *   /dev page was removed.
 *
 * The store (./store) and the ready check-ups (./packages) are the other
 * drafts. No route renders them since their /dev pages were removed. The
 * check-ups draft shares the old plan card and the single-test tables.
 */

import { useId, useState } from "react"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { CheckmarkIcon, WarningIcon } from "@/components/ui/icons"
import { TestsStepsTimeline } from "@/components/ui/tests-steps-timeline"
import { blobMediaUrl } from "@/lib/blob-url"
import { useTranslations } from "@/lib/intl/use-translations"
import { isAvailable } from "@/lib/availability"
import { cn } from "@/lib/utils"
import type { BookingScan } from "../home/home-v4-data"
import { HomeV4Insurance } from "../home/home-v4-insurance"
import { HOMEPAGE_FAQ_KEYS } from "@/app/(frontend)/[country]/[locale]/_components/faq-section"
import { HomeV4Faq } from "../home/home-v4-faq"
import { HomeV4Close } from "../home/home-v4-close"
import type { HomeV4Insurer } from "../home/home-v4-insurers"
import {
  SavePill,
  DetailsPill,
  HAIRLINE,
  InfoSheet,
  ItemPicture,
  useInfoContent,
} from "./pricing-v5-booking"
import { usePricingV5Copy } from "./pricing-v5-copy"
import { MriPlanCards, StatusPill, infoLabels } from "./pricing-v5-mri-cards"
import { Button } from "@/components/ui/button"
import { testDetails } from "./pricing-v5-items"
import { MEMBERSHIP_FIRST_BIOMARKERS } from "@/data/membership-first-blood-addons"
import type { PricingV5Data } from "./pricing-v5-page-data"
import { BaselinePlanCard } from "./pricing-v5-plan-card"
import {
  BookButton,
  IMAGING_KEYS,
  Included,
  OneTimeLink,
  PlanPricesProvider,
  SECTION,
  useCheckUp,
  usePlanPrices,
  useSummary,
  type CheckUp,
  type Copy,
} from "./pricing-v5-shared"

export type PickVariant = "all" | "scan"

const TRACK = "dev/pricing-v5"

/* ── 01 The plan ───────────────────────────────────────────────────────── */

/** The page's heading, with the plan's yearly price in the line under it
 *  (Eugene, 30.09: "say something about prices", "in the subheading"). */
function Intro({ INTRO }: { INTRO: Copy["INTRO"] }) {
  const { money, plan } = usePlanPrices()
  return (
    <div className="text-center">
      <p className="type-label-lg text-neutral-500">{INTRO.eyebrow}</p>
      <h1 className="type-display-lg-emphasized mx-auto mt-3 max-w-[960px] text-balance text-neutral-900">
        {INTRO.heading}
      </h1>
      <p className="type-body-xl mx-auto mt-6 max-w-[640px] text-neutral-600">
        {INTRO.body(money(plan))}
      </p>
    </div>
  )
}

/**
 * The booking button under the plan card (with the scan picked so far),
 * then one short line: billing terms and the way out to today's one-time
 * flow, where Core is (Eugene, 30.09: "shorten it").
 */
function PlanFootnote({
  PLAN,
  cta,
  track,
}: {
  PLAN: Copy["PLAN"]
  cta?: { label: string; scan: BookingScan }
  track: string
}) {
  return (
    <div className="mt-6 text-center">
      {cta && (
        <BookButton
          scan={cta.scan}
          label={cta.label}
          trackingLabel="pricing_v5_plan_cta"
          trackingLocation={track}
          className="mb-6 inline-block"
        />
      )}
      <p className="type-body-md text-balance text-neutral-500">
        {PLAN.billingShort} ·{" "}
        <OneTimeLink
          trackingLabel="pricing_v5_one_time"
          trackingLocation={track}
        >
          {PLAN.oneTimeShort}
        </OneTimeLink>
      </p>
    </div>
  )
}

/**
 * The plan as a table, the check-ups draft's card. The builder has the
 * booking's card above.
 */
export function PlanCard({
  PLAN,
  track = TRACK,
}: {
  PLAN: Copy["PLAN"]
  track?: string
}) {
  const { money, tier: tierAmount } = usePlanPrices()
  const last = PLAN.tiers.length - 1

  return (
    <div className="mx-auto max-w-[1100px]">
      <div className="rounded-[32px] bg-white p-6 shadow-[0_12px_48px_rgba(0,0,0,0.04)] ring-1 ring-neutral-100 md:grid md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] md:gap-12 md:p-12">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="type-label-lg text-neutral-500">{PLAN.eyebrow}</p>
            {/* The plan is the one line of the check-up that cannot be
                taken out, so it has no Add button. */}
            <p className="type-body-sm bg-deep-green-100 text-deep-green-700 inline-flex items-center gap-1.5 rounded-full px-3 py-1">
              <CheckmarkIcon width={16} height={16} />
              {PLAN.alwaysIn}
            </p>
          </div>
          <h2 className="type-headline-md-emphasized mt-2 text-neutral-900">
            {PLAN.heading}
          </h2>
          <table className="mt-6 w-full border-collapse">
            <thead>
              <tr className="border-b border-neutral-100">
                <th
                  scope="col"
                  className="type-body-md pb-3 text-left font-normal text-neutral-500"
                >
                  {PLAN.colRhythm}
                </th>
                <th
                  scope="col"
                  className="type-body-md pb-3 text-right font-normal text-neutral-500"
                >
                  {PLAN.colPrice}
                </th>
              </tr>
            </thead>
            <tbody>
              {PLAN.tiers.map((tier, i) => (
                <tr
                  key={tier.name}
                  className={cn(i < last && "border-b border-neutral-100")}
                >
                  <th
                    scope="row"
                    className="py-4 pr-6 text-left align-top font-normal"
                  >
                    <span className="type-body-lg-emphasized block text-neutral-900">
                      {tier.name}
                    </span>
                    <span className="type-body-md mt-0.5 block text-neutral-500">
                      {tier.desc}
                    </span>
                  </th>
                  <td className="type-body-lg-emphasized py-4 text-right align-top whitespace-nowrap text-neutral-900">
                    {money(tierAmount(tier.id))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="type-body-md mt-4 text-neutral-600">{PLAN.samePrice}</p>
        </div>
        <div className="mt-10 border-t border-neutral-100 pt-8 md:mt-0 md:border-t-0 md:border-l md:pt-0 md:pl-12">
          <p className="type-title-md text-neutral-900">
            {PLAN.includedHeading}
          </p>
          <div className="mt-5">
            <Included items={PLAN.included} />
          </div>
        </div>
      </div>
      <PlanFootnote PLAN={PLAN} track={track} />
    </div>
  )
}

/* ── 02 An MRI on top ──────────────────────────────────────────────────── */

function SectionIntro({
  eyebrow,
  heading,
  body,
}: {
  eyebrow?: string
  heading: string
  body: string
}) {
  return (
    <div className="text-center">
      {eyebrow && <p className="type-label-lg text-neutral-500">{eyebrow}</p>}
      <h2
        className={cn(
          "type-display-md-emphasized text-neutral-900",
          eyebrow && "mt-2"
        )}
      >
        {heading}
      </h2>
      <p className="type-body-xl mx-auto mt-4 max-w-[620px] text-neutral-600">
        {body}
      </p>
    </div>
  )
}

/**
 * A thin line between a section's cards and the next section's text
 * (Eugene, 30.09: "shaded lines", "add below too"). Under a section's text
 * it runs down into its cards and fades out; above a heading it comes in
 * from the cards before it, faded at the top and solid at the heading, so
 * the page reads card, line, heading, line, cards (Eugene, 09.10, bringing
 * back the lines on top). No numbers, so it does not read as how it works.
 */
function StepLine({
  into = false,
  className,
}: {
  /** Above a heading: faded at the top, solid at the bottom. */
  into?: boolean
  className?: string
}) {
  return (
    <div aria-hidden className={cn("flex justify-center", className)}>
      <span
        className={cn(
          "h-16 w-px from-transparent to-neutral-300 md:h-20",
          into ? "bg-gradient-to-b" : "bg-gradient-to-t"
        )}
      />
    </div>
  )
}

function MriTiles({
  copy,
  checkUp,
  className,
}: {
  copy: Copy
  checkUp: CheckUp
  className?: string
}) {
  const { MRI } = copy
  return (
    // As wide as the plan card and the single tests (Eugene, 30.09).
    <div className={cn("mx-auto max-w-[960px]", className)}>
      <SectionIntro
        // One phrase, no eyebrow (Eugene, 30.09: "Go deeper with a
        // full-body MRI").
        heading={MRI.heading}
        body={MRI.body}
      />
      <StepLine className="mt-4" />
      {/* Side by side from lg, where a card is wide enough for its price
          on one line; one a row below that. 8px between the two cards
          (Eugene, 30.09: "блоки поближе"). */}
      {/* Three cards reach past the column on a wide screen, so each keeps
          at least a phone's width (Eugene, 12.10). */}
      <MriPlanCards
        copy={copy}
        checkUp={checkUp}
        track={TRACK}
        className="xl:-mx-[120px]"
      />
    </div>
  )
}

/* ── 03 Single tests ───────────────────────────────────────────────────── */

type TestItem = Copy["ADDONS"]["groups"][number]["items"][number]

/**
 * One single test as a table row: its tube or scan small, its name and
 * line, "More info", then the plan price and the price without it. With
 * `checkUp` the row ends in an Add button; the row itself picks nothing
 * (Eugene, 30.09). A scan that goes with an MRI is priced as the booking
 * prices it, "+" on top of the scan.
 */
function TestRow({
  item,
  copy,
  checkUp,
  card,
}: {
  item: TestItem
  copy: Pick<Copy, "ADDONS" | "PICK" | "INFO">
  checkUp?: CheckUp
  /** A card in a two-column grid instead of a full-width row: the copy on
   *  top, "More info" and the prices along the bottom (Eugene, 01.10). */
  card?: boolean
}) {
  const { ADDONS, INFO } = copy
  const { money, list, member, availability, biomarkersOf } = usePlanPrices()
  const labels = infoLabels(copy)
  const read = useInfoContent()
  const pt = useTranslations("pricing") as unknown as (key: string) => string
  const [open, setOpen] = useState(false)
  const details = testDetails(item.key)
  // The site's own tests are not in the CMS: their biomarkers as the
  // booking lists them.
  const ownBiomarkers = MEMBERSHIP_FIRST_BIOMARKERS[item.key]
  const state = availability(details.gate)
  const status =
    state === "not_available"
      ? INFO.notAvailable
      : state === "coming_soon" || state === "waitlist"
        ? INFO.comingSoon
        : undefined
  const imaging = IMAGING_KEYS.has(item.key)
  const pickable = checkUp && !status
  const picked = !!checkUp?.tests.includes(item.key)
  const planPrice = member(item.key)
  const listPrice = list(item.key)
  const discounted =
    listPrice != null && planPrice != null && listPrice > planPrice
  const toggle = () => checkUp?.toggleTest(item.key)
  // The row says what the test is for in the pop-up's own words (Eugene,
  // 30.09: the one-liners left the row mostly empty); three lines at most.
  const content = details.info
    ? read(details.info, {
        name: item.name,
        image: details.image,
        price: {
          now: money(planPrice),
          was: discounted ? money(listPrice) : undefined,
          wasTip: INFO.withoutPlan,
        },
        banner:
          status ?? (imaging && !checkUp?.mri ? INFO.needsMri : undefined),
        biomarkers:
          biomarkersOf(details.info.cmsPackageId) ??
          (ownBiomarkers && [
            {
              category: pt(ownBiomarkers.categoryKey),
              items: ownBiomarkers.itemKeys.map((key) => pt(key)),
            },
          ]),
      })
    : undefined
  const description = content?.description ?? item.desc
  const sheet = content && (
    <InfoSheet
      content={content}
      open={open}
      onClose={() => setOpen(false)}
      labels={labels}
      action={
        pickable
          ? {
              picked,
              onToggle: toggle,
              disabled: imaging && !checkUp.mri,
            }
          : undefined
      }
    />
  )

  if (card) {
    const price = status ? (
      <StatusPill label={status} />
    ) : (
      <p className="flex flex-col sm:items-end sm:text-right">
        <span className="sr-only">{ADDONS.planPrice} </span>
        <span className="type-title-md whitespace-nowrap text-neutral-900">
          + {money(planPrice)}
          <span aria-hidden>*</span>
        </span>
        {discounted && (
          <span className="type-body-md whitespace-nowrap text-neutral-500 line-through">
            <span className="sr-only">{ADDONS.regularPrice} </span>
            {money(listPrice)}
          </span>
        )}
      </p>
    )
    return (
      // The pricing page's row as a card for the two columns: the tube,
      // name and line with More info and Save under them, the prices at
      // the end; on a phone the prices go under the copy (Eugene, 01.10).
      <li
        className={cn(
          "flex items-center gap-4 rounded-2xl bg-white p-4",
          HAIRLINE
        )}
      >
        <div className="-my-2 -ml-2 flex w-[72px] shrink-0 items-center justify-center self-stretch sm:w-[94px]">
          <ItemPicture
            src={details.image}
            px={94}
            kind={details.kind}
            className={cn(
              "size-[72px] sm:size-[94px]",
              details.kind === "tube" && "bg-transparent"
            )}
          />
        </div>
        <div className="min-w-0 flex-1 py-1">
          <p className="type-body-xl-emphasized text-neutral-900">
            {item.name}
          </p>
          <p className="type-body-md mt-1 text-neutral-600">{item.desc}</p>
          <div className="mt-2 sm:hidden">{price}</div>
          {(content || pickable) && (
            <div className="mt-0.5 -mb-2 flex flex-wrap items-center gap-x-3">
              {content && (
                <DetailsPill
                  label={INFO.more}
                  onClick={() => setOpen(true)}
                  size="mini"
                />
              )}
              {pickable && (
                <SavePill
                  picked={picked}
                  onToggle={toggle}
                  labels={labels}
                  name={item.name}
                />
              )}
            </div>
          )}
        </div>
        <div className="hidden shrink-0 sm:block">{price}</div>
        {sheet}
      </li>
    )
  }

  return (
    // One segment of the group's card: full width, 2px corners, the card's
    // own rounding does the outside (Eugene, 30.09), with a little more
    // air above and below (Eugene, 01.10).
    <li className="flex gap-4 rounded-[2px] bg-white px-4 py-5 md:items-center md:gap-6 md:px-6 md:py-6">
      {/* The booking's add-on tile: a tube at 72px, 94px from sm. A tube
          stands on the row itself, photos keep their white tile. */}
      <ItemPicture
        src={details.image}
        px={94}
        kind={details.kind}
        className={cn(
          "size-[72px] shrink-0 sm:size-[94px]",
          details.kind === "tube" && "bg-transparent"
        )}
      />
      {/* On a phone the prices and Add go under the copy; from md they end
          the row. */}
      <div className="flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center md:gap-6">
        {/* Title 18 medium (Eugene, 01.10), the booking's type otherwise:, the description 14 in
            neutral-600, prices 16 medium beside a struck-through regular. */}
        <div className="min-w-0 flex-1 py-1">
          <p className="type-body-xl-emphasized text-neutral-900">
            {item.name}
          </p>
          <p className="type-body-md mt-1 line-clamp-3 max-w-[620px] text-neutral-600">
            {description}
          </p>
          {(content || pickable || (checkUp && imaging)) && (
            // "More info" and the MRI hint share a line, the pill a little
            // clear of the copy (Eugene, 30.09). Its 40px target is taller
            // than the pill, so the pill stands 10px under the copy and the
            // row gets the extra 8px back below.
            <div className="mt-0.5 -mb-2 flex flex-wrap items-center gap-x-3">
              {content && (
                <DetailsPill
                  label={INFO.more}
                  onClick={() => setOpen(true)}
                  size="mini"
                />
              )}
              {/* Save sits beside More info at its size, not at the end
                  of the row (Eugene, 01.10). */}
              {pickable && (
                <SavePill
                  picked={picked}
                  onToggle={toggle}
                  labels={labels}
                  name={item.name}
                />
              )}
              {/* Always in the DOM, empty until needed, so screen readers
                  hear the hint appear. */}
              {checkUp && imaging && (
                <p
                  role="status"
                  className="type-body-md flex items-center gap-1.5 text-amber-700"
                >
                  {checkUp.hint === item.key && (
                    <>
                      <WarningIcon
                        width={18}
                        height={18}
                        className="shrink-0"
                      />
                      {ADDONS.needsMri}
                    </>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
        {/* The two prices stacked, so price and Add fit beside each other
            under the copy on a phone; at 320px Add drops under them. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 md:flex-nowrap md:justify-end md:gap-6">
          {status ? (
            <StatusPill label={status} />
          ) : (
            <p className="flex flex-col md:items-end md:text-right">
              <span className="sr-only">{ADDONS.planPrice} </span>
              <span className="type-title-md whitespace-nowrap text-neutral-900">
                {/* Every test goes on top of the plan, once (Eugene,
                    30.09), so every price carries the "+". */}
                + {money(planPrice)}
                <span aria-hidden>*</span>
              </span>
              {discounted && (
                <span className="type-body-md whitespace-nowrap text-neutral-500 line-through">
                  <span className="sr-only">{ADDONS.regularPrice} </span>
                  {money(listPrice)}
                </span>
              )}
            </p>
          )}
        </div>
      </div>
      {sheet}
    </li>
  )
}

/**
 * The single tests, one card per group. Each card is a stack of full-width
 * segments with 2px corners and a 2px gap, the card's own rounding on the
 * outside (Eugene, 30.09). A row shows the plan price with the regular one
 * struck through beside it, as the booking's add-on tiles do. With
 * `checkUp` each row has an Add button; without it the cards are a price
 * list and the intro says the tests are added in the booking. Every row has
 * its "More info".
 */
/** How many cards the test pages' list shows before "View N more". */
const FOLD_DESKTOP = 6
const FOLD_PHONE = 4

export function SingleTests({
  ADDONS,
  PICK,
  INFO,
  checkUp,
  only,
  exclude,
  className,
  trackingLocation = "pricing_v5_single_tests",
}: {
  ADDONS: Copy["ADDONS"]
  PICK: Copy["PICK"]
  INFO: Copy["INFO"]
  checkUp?: CheckUp
  /** "imaging": the scans that go with an MRI alone, under their own
   *  heading, for the MRI test page. "blood": the blood tests alone, for
   *  the blood test page (Eugene, 01.10). */
  only?: "imaging" | "blood"
  /** A test left out of the list, the page's own. */
  exclude?: string
  className?: string
  /** The "without the plan" link's `cta_click` location. */
  trackingLocation?: string
}) {
  const { code: country } = useCountry()
  const locale = useLocale()
  const { availability } = usePlanPrices()
  const [showAll, setShowAll] = useState(false)
  const expandedListId = useId()
  // What the market does not sell at all is not listed, as on /pricing.
  const listed = (key: string) =>
    isAvailable(testDetails(key).info?.availability, country, locale) &&
    availability(testDetails(key).gate) !== "hidden" &&
    (only !== "imaging" || IMAGING_KEYS.has(key)) &&
    (only !== "blood" || !IMAGING_KEYS.has(key)) &&
    key !== exclude
  // Alone on a test page, the card is the section.
  const imagingOnly = only !== undefined

  // The blood test page: a heading and a two-column grid of cards, as wide
  // as the plan card above it (Eugene, 01.10: the rows took too much room).
  // The scans on the MRI pages take the same cards, with Save, so every
  // test page lists its extras one way (Eugene, 01.10).
  if (only === "blood" || (only === "imaging" && checkUp)) {
    const items: TestItem[] = ADDONS.groups
      .flatMap((group): TestItem[] => group.items)
      .filter((item) => listed(item.key))
    // Three rows of two on a computer, four cards on a phone, the rest
    // behind "View N more" (Eugene, 02.10: the full list was too long).
    const hiddenDesktop = Math.max(0, items.length - FOLD_DESKTOP)
    const hiddenPhone = Math.max(0, items.length - FOLD_PHONE)
    return (
      <div className={cn("mx-auto max-w-[1120px]", className)}>
        <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-4">
          <h2 className="type-headline-md-emphasized text-neutral-900">
            {only === "blood"
              ? ADDONS.bloodMore
              : (ADDONS.groups.find((group) =>
                  group.items.some((item) => IMAGING_KEYS.has(item.key))
                )?.label ?? ADDONS.heading)}
          </h2>
          <PricesHeader ADDONS={ADDONS} />
        </div>
        <ul className="mt-6 grid gap-3 lg:grid-cols-2">
          {items.slice(0, FOLD_PHONE).map((item) => (
            <TestRow
              key={item.key}
              item={item}
              copy={{ ADDONS, PICK, INFO }}
              checkUp={checkUp}
              card
            />
          ))}
        </ul>
        {hiddenPhone > 0 && (
          <>
            <div id={expandedListId}>
              {[
                { items: items.slice(FOLD_PHONE, FOLD_DESKTOP), desktop: true },
                { items: items.slice(FOLD_DESKTOP), desktop: false },
              ]
                .filter((group) => group.items.length > 0)
                .map((group) => (
                  <div
                    key={group.desktop ? "desktop-preview" : "remaining"}
                    inert={!showAll && !group.desktop}
                    className={cn(
                      "grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                      showAll
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0",
                      group.desktop &&
                        "md:visible md:grid-rows-[1fr] md:opacity-100",
                      !showAll &&
                        (group.desktop ? "max-md:invisible" : "invisible")
                    )}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <ul className="grid gap-3 pt-3 lg:grid-cols-2">
                        {group.items.map((item) => (
                          <TestRow
                            key={item.key}
                            item={item}
                            copy={{ ADDONS, PICK, INFO }}
                            checkUp={checkUp}
                            card
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
            </div>
            <div
              inert={showAll}
              aria-hidden={showAll}
              className={cn(
                "grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                showAll
                  ? "grid-rows-[0fr] opacity-0"
                  : "grid-rows-[1fr] opacity-100",
                hiddenDesktop === 0 && "md:hidden"
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="flex items-center justify-center gap-2 pt-6">
                  <span
                    aria-hidden="true"
                    className="h-px max-w-40 flex-1 bg-gradient-to-l from-[#e8e8e8] to-transparent"
                  />
                  <Button
                    variant="tonal"
                    className="h-10 shrink-0 bg-[#efefef] px-6 py-2"
                    aria-expanded={showAll}
                    aria-controls={expandedListId}
                    onClick={() => setShowAll(true)}
                  >
                    <span className="md:hidden">
                      {ADDONS.viewMore(hiddenPhone)}
                    </span>
                    <span className="max-md:hidden">
                      {ADDONS.viewMore(hiddenDesktop)}
                    </span>
                  </Button>
                  <span
                    aria-hidden="true"
                    className="h-px max-w-40 flex-1 bg-gradient-to-r from-[#e8e8e8] to-transparent"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={cn("mx-auto max-w-[960px]", className)}>
      {/* Alone on the MRI page, the card is the section: its own header
          names it, as on /pricing (Eugene, 30.09). */}
      {!imagingOnly && (
        <>
          <div className="text-center">
            {/* One phrase, no eyebrow, as the MRI's (Eugene, 30.09). */}
            <h2 className="type-display-md-emphasized text-neutral-900">
              {ADDONS.heading}
            </h2>
            <p className="type-body-xl mx-auto mt-4 max-w-[620px] text-neutral-600">
              {ADDONS.body}
              {!checkUp && ` ${ADDONS.inBooking}`}
            </p>
          </div>
          <StepLine className="mt-4" />
        </>
      )}
      <div className="flex flex-col gap-6">
        {ADDONS.groups.map((group) => {
          const items = group.items.filter((item) => listed(item.key))
          if (items.length === 0) return null
          return (
            <div
              key={group.label}
              className={cn(
                "flex flex-col gap-[2px] overflow-hidden rounded-[20px]",
                HAIRLINE
              )}
            >
              {/* On a phone the prices sit under each row's copy, so what
                  they are is said under the group's name; from md it heads
                  the price column. */}
              <div className="flex flex-col gap-1 rounded-[2px] bg-white px-4 py-6 md:flex-row md:items-baseline md:justify-between md:gap-4 md:px-6 md:py-8">
                <div className="min-w-0">
                  <p className="type-title-lg text-neutral-900">
                    {group.label}
                  </p>
                  {/* Full examinations, not add-ons (Eugene, 30.09), in the MRI's visit:
                      one phrase (Eugene, 30.09). */}
                  <p className="type-body-md mt-1 text-pretty text-neutral-600">
                    {items.some((item) => IMAGING_KEYS.has(item.key))
                      ? ADDONS.imagingSameVisit
                      : ADDONS.bloodSameDraw}
                  </p>
                </div>
                {/* Flush with the card's right edge (Eugene, 30.09: "just on
                    the right"). */}
                <PricesHeader ADDONS={ADDONS} />
              </div>
              <ul className="flex flex-col gap-[2px]">
                {items.map((item) => (
                  <TestRow
                    key={item.key}
                    item={item}
                    copy={{ ADDONS, PICK, INFO }}
                    checkUp={checkUp}
                  />
                ))}
              </ul>
            </div>
          )
        })}
      </div>
      {/* One way to buy them without the plan, under both lists, in
          today's booking flow (Eugene, 30.09). */}
      {!imagingOnly && (
        <p className="type-body-md mt-6 text-center text-neutral-500">
          <OneTimeLink
            trackingLabel="pricing_v5_tests_one_off"
            trackingLocation={trackingLocation}
          >
            {ADDONS.oneOff}
          </OneTimeLink>
        </p>
      )}
    </div>
  )
}

/** Over the price column: what the prices are, and what the "*" on them
 *  means, on top rather than under the list (Eugene, 01.10). */
function PricesHeader({ ADDONS }: { ADDONS: Copy["ADDONS"] }) {
  return (
    <div className="shrink-0 text-neutral-500 md:text-right">
      <p className="type-body-md">{ADDONS.pricesHeader}</p>
      <p className="type-body-md">*{ADDONS.membersNote}</p>
    </div>
  )
}

/** The pricing page's closing block, the homepage's "Not sure where to
 *  start?" with the quiz, for the test pages on the membership-first site
 *  (Eugene, 01.10: it replaces their photo banner). */
export function PlanSiteClose({
  trackingLocation,
  scan = false,
  productId,
}: {
  /** Its booking button's `cta_click` location, when not the block's own. */
  trackingLocation?: string
  /** A scan's page: the results line gives the MRI's 7 to 10 days. */
  scan?: boolean
  /** The test page's product, which its booking button books. */
  productId?: string
} = {}) {
  const { CLOSE } = usePricingV5Copy()
  return (
    <HomeV4Close
      section={SECTION}
      lede={CLOSE.lede}
      trackingLocation={trackingLocation}
      results={scan ? CLOSE.mriResults : undefined}
      productId={productId}
      scan={scan}
    />
  )
}

/** The blood tests that go on top of the plan, as a price list, for the
 *  blood test page on the membership-first site (Eugene, 01.10). Inside
 *  MriPlanScope. */
export function PlanBloodAddons({
  exclude,
  className,
  trackingLocation,
}: {
  /** The page's own test, left out of its list. */
  exclude?: string
  className?: string
  /** The "without the plan" link's `cta_click` location, when not its own. */
  trackingLocation?: string
}) {
  const { ADDONS, PICK, INFO } = usePricingV5Copy()
  // The same saved check-up as /pricing and the navbar (Eugene, 01.10).
  const checkUp = useCheckUp()
  return (
    <SingleTests
      ADDONS={ADDONS}
      PICK={PICK}
      INFO={INFO}
      checkUp={checkUp}
      only="blood"
      exclude={exclude}
      className={className}
      trackingLocation={trackingLocation}
    />
  )
}

/** The scans that go with an MRI, for the MRI test pages on the
 *  membership-first site: the blood test page's cards with Save, as wide
 *  as the two MRI cards (Eugene, 30.09 and 01.10). Inside MriPlanScope. */
export function MriPlanImaging({
  exclude,
  className,
}: {
  /** The page's own scan, left out of its list. */
  exclude?: string
  className?: string
}) {
  const { ADDONS, PICK, INFO } = usePricingV5Copy()
  const checkUp = useCheckUp()
  return (
    <SingleTests
      ADDONS={ADDONS}
      PICK={PICK}
      INFO={INFO}
      checkUp={checkUp}
      only="imaging"
      exclude={exclude}
      className={cn("max-w-[960px]", className)}
    />
  )
}

/* ── 04 How it works ───────────────────────────────────────────────────── */

/**
 * How it works in four steps, as the plan now takes an MRI too (Eugene,
 * 30.09: "four steps, because the MRI is included"): before, the blood
 * test, the scan in the same visit, the results. The test pages' scroll-
 * linked steps. The pictures are placeholders from the hormone test's film
 * series and the MRI pages until the new ones are in.
 */
const HOW_STEP_IMAGES = [
  blobMediaUrl(
    "/images/marketing/hormone-women-film-20260930/01-before-v1.png"
  ),
  blobMediaUrl(
    "/images/marketing/hormone-women-film-20260930/02-clinic-v1.png"
  ),
  blobMediaUrl("/images/mri/walkthrough/step-scan.png"),
  blobMediaUrl(
    "/images/marketing/hormone-women-film-20260930/03-report-v1.png"
  ),
]

/** How it works, after the prices, with the page's one booking button. */
function HowItWorks({
  HOW,
  cta,
}: {
  HOW: Copy["HOW"]
  cta: { label: string; scan: BookingScan; addOnIds?: readonly string[] }
}) {
  const tt = useTranslations("tests")
  return (
    <TestsStepsTimeline
      label={HOW.heading}
      headingKey="howStepsHeading"
      stepWord={tt("mriWalkStepWord")}
      namespace="pricingV5"
      imageQuality={90}
      tone="plan"
      steps={HOW_STEP_IMAGES.map((image, i) => ({
        titleKey: `howStep${i + 1}Title`,
        descKey: `howStep${i + 1}Desc`,
        image,
      }))}
      cta={
        <BookButton
          scan={cta.scan}
          addOnIds={cta.addOnIds}
          label={cta.label}
          trackingLabel="pricing_v5_steps_cta"
          trackingLocation={TRACK}
          className="block w-full sm:inline-block sm:w-auto"
          buttonClassName="w-full sm:w-auto"
        />
      }
    />
  )
}

/* ── Page ──────────────────────────────────────────────────────────────── */

type PageProps = {
  variant: PickVariant
  insurers: HomeV4Insurer[]
  /** The CMS and price data the page loaded on the server. */
  data?: PricingV5Data
}

/** The homepage's questions about paying, in the order a buyer asks them. */
const PRICING_FAQ = [
  HOMEPAGE_FAQ_KEYS[4],
  HOMEPAGE_FAQ_KEYS[2],
  HOMEPAGE_FAQ_KEYS[3],
  HOMEPAGE_FAQ_KEYS[8],
] as const

export default function PricingV5Client(props: PageProps) {
  return (
    <PlanPricesProvider data={props.data}>
      <BuilderPage {...props} />
    </PlanPricesProvider>
  )
}

function BuilderPage({ variant, insurers }: PageProps) {
  const copy = usePricingV5Copy()
  const { INTRO, HOW, MRI, ADDONS, PICK, INFO, SUMMARY, CLOSE } = copy
  const checkUp = useCheckUp()
  // The saved tests ride along on every Book button here, as on the
  // navbar's and the summary's (audit, 09.10).
  const { scan, addOnIds } = useSummary(checkUp, { MRI, ADDONS })

  return (
    <>
      {/* One ground for the plan, the MRI and the tests, as the booking's
          add-ons step: white cards on the page grey. */}
      <div className="bg-background">
        <section className="pt-10 md:pt-12">
          <div className={SECTION}>
            <Intro INTRO={INTRO} />
            <div className="mt-10 md:mt-14">
              <BaselinePlanCard
                copy={copy}
                cta={{ label: SUMMARY.ctaShort, scan, addOnIds }}
                track={TRACK}
              />
            </div>
          </div>
        </section>
        {/* A line up to the card above each heading, then the heading,
            then a line down into its cards (Eugene, 09.10). */}
        <section className="pt-6 md:pt-8">
          <div className={SECTION}>
            <StepLine into className="mb-4" />
            <MriTiles copy={copy} checkUp={checkUp} />
          </div>
        </section>
        <section className="pt-6 pb-16 md:pt-8 md:pb-[96px]">
          <div className={SECTION}>
            <StepLine into className="mb-4" />
            <SingleTests
              ADDONS={ADDONS}
              PICK={PICK}
              INFO={INFO}
              checkUp={variant === "all" ? checkUp : undefined}
            />
          </div>
        </section>
      </div>
      <HowItWorks HOW={HOW} cta={{ label: SUMMARY.ctaShort, scan, addOnIds }} />
      {/* Grey insurance, white questions: the steps above went white
          (Eugene, 02.10), so the bands keep alternating. */}
      <HomeV4Insurance section={SECTION} insurers={insurers} tone="grey" />
      {/* Only the questions about paying (Eugene, 30.09): insurance, the
          couples discount, employers and which check-up to pick. */}
      <div className="bg-white">
        <HomeV4Faq section={SECTION} keys={PRICING_FAQ} />
      </div>
      <HomeV4Close section={SECTION} lede={CLOSE.lede} />
      {/* No bar at the bottom: the check-up is the navbar's cart, which
          the page shell mounts on every page of the site (SiteCart). */}
    </>
  )
}
